import { Upload, Loader2, FileText, X, CheckCircle } from 'lucide-react';

// ─────────────────────────────────────────
// STEP 1 — DECLARATIONS UPLOAD
// Lets the client drop in their dec pages. The parent runs the
// AI analysis and passes the results down via props.
// ─────────────────────────────────────────
export default function Step1Declarations({
  declFiles,
  fileInputRef,
  analyzing,
  analyzed,
  analyzeError,
  aiFields,
  onUpload,
  onRemoveFile,
  onSkip,
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
      <div>
        <h2 className="text-base font-semibold text-slate-800 mb-1">Upload Your Declaration Pages</h2>
        <p className="text-sm text-slate-500">
          Upload your current insurance declaration pages and our AI will automatically fill in your information.
          You'll be able to review and edit everything before submitting.
        </p>
      </div>

      <label className="flex flex-col items-center justify-center gap-3 w-full border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-slate-50 rounded-2xl py-8 cursor-pointer transition">
        <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
          <Upload size={22} className="text-blue-600" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-slate-700">Click to upload declaration pages</p>
          <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG accepted — upload multiple files</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg"
          className="hidden"
          onChange={onUpload}
        />
      </label>

      {declFiles.length > 0 && (
        <div className="space-y-2">
          {declFiles.map((f, i) => (
            <div
              key={i}
              className={`flex items-center justify-between rounded-xl px-3 py-2.5 border transition ${
                analyzing ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${analyzing ? 'bg-purple-100' : 'bg-blue-100'}`}>
                  {analyzing
                    ? <Loader2 size={13} className="text-purple-600 animate-spin" />
                    : <FileText size={13} className="text-blue-600" />}
                </div>
                <div className="min-w-0">
                  <span className="text-sm text-slate-700 truncate block max-w-[280px]">{f.name}</span>
                  {analyzing && <span className="text-xs text-purple-500">Analyzing with AI...</span>}
                </div>
              </div>
              <button
                onClick={() => onRemoveFile(i)}
                className="p-1 text-slate-400 hover:text-red-500 transition ml-2 shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {analyzed && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <CheckCircle size={18} className="text-green-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Information extracted successfully</p>
            <p className="text-xs text-green-700 mt-0.5">
              We've pre-filled your form with data from your declaration pages. Please review each section carefully and make any corrections.
            </p>
            {aiFields && aiFields.length > 0 && (
              <div className="mt-2 space-y-1">
                {aiFields.map((policy, i) => (
                  <p key={i} className="text-xs text-green-700">
                    • {[policy.carrier, policy.coverageType, policy.policyNumber].filter(Boolean).join(' — ')}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {analyzeError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
          {analyzeError}
        </div>
      )}

      <button
        onClick={onSkip}
        className="w-full text-center text-xs text-slate-400 hover:text-slate-600 transition py-1"
      >
        Skip — I'll fill in the form manually
      </button>
    </div>
  );
}